import React, { useContext, useEffect } from "react";
import { FiltersRequest, TradeGraphType } from "@shared/types";
import BarChart from "common/Charts/BarChart.tsx";
import LineChart from "common/Charts/LineChart/LineChart.tsx";
import styled, { useTheme } from "styled-components";
import Card from "common/Boxes/Card.tsx";
import Title from "common/Typography/Title.tsx";
import ChartCardLayout from "common/Layouts/ChartCardLayout.jsx";
import useTradesGraphData from "features/trades/TradesCharts/useTradesGraphData.ts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import NewLookChartCard from "features/trades/NewLook/NewLookChartCard.tsx";

interface TradesGraphCardProps {
  title: string;
  graphType: TradeGraphType;
  useBarChart?: boolean;
  filters?: FiltersRequest;
  precision: number;
  setChartFetching?: (chartFetching: boolean) => void;
  /** New look: title row and chart only, for the overview panel. */
  flush?: boolean;
}

const TradesGraphCard = ({
  title,
  graphType,
  useBarChart,
  filters,
  precision,
  setChartFetching,
  flush,
}: TradesGraphCardProps) => {
  const {
    displayCurrency,
    timeRange,
    setTimeRange,
    isFetching: baseIsFetching,
    isSuccess,
    graphTypeToData,
    refetch,
  } = useTradesGraphData({ filters, precision });
  const data = graphTypeToData[graphType];
  const { getTradesQuery } = useContext(TradesPageContext) || {};
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isNewLook } = useTheme();
  const isFetching = baseIsFetching && !isBottomNavigator;

  useEffect(() => {
    if (getTradesQuery?.isFetching) {
      try {
        refetch();
      } catch {}
    }
  }, [getTradesQuery?.isFetching]);

  useEffect(() => {
    if (graphType === TradeGraphType.PROFIT_BY_DAY) {
      setChartFetching?.(isFetching);
    }
  }, [isFetching]);

  const chartProps = {
    data,
    loading: isFetching,
    displayCurrency,
    precision,
    isSuccess,
    style: { height: "100%" },
  };
  const chart = (
    <ChartWrapper>
      {useBarChart ? (
        <BarChart {...chartProps} usePositiveNegativeColorScheme={true} />
      ) : (
        <LineChart {...chartProps} />
      )}
    </ChartWrapper>
  );

  if (isNewLook) {
    return (
      <NewLookChartCard
        title={title}
        chart={chart}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        flush={flush}
      />
    );
  }

  return (
    <ChartCard>
      <ChartCardLayout
        change={<Title level={3}>{title}</Title>}
        chart={chart}
        selectedTimeRange={timeRange}
        setTimeRange={setTimeRange}
        buttonSize="medium"
        autoLayoutHeight={true}
      />
    </ChartCard>
  );
};

export default TradesGraphCard;

const ChartCard = styled(Card)`
  overflow: hidden;
`;
const ChartWrapper = styled.div`
  padding-top: 8px;
  height: 100%;
`;
